/**
* Содержит актуальные цены на все доступные свойства Проекта
* При изменении проекта - обновляет цены.
**/
import { __decorate } from "tslib";
import Reportable from '../decorators/reportable';
import DefaultReturn from '../decorators/defaultReturn';
import EventEmitter from 'events';
import evaluate from './eval.expr-eval';
const PRICE_ALWAYS_ZERO = ['size'];
const PRICE_ONLY_IN_STEP = ['wrapper', 'face'];
class CalculatorPrice extends EventEmitter {
    calculator;
    project;
    prices = new Map();
    pricesOfBagets = new Map();
    constructor(calculator, project) {
        super();
        this.calculator = calculator;
        this.project = project;
        project.on('propertyHided', () => this.updateAllPrices());
        project.on('propertyShowed', () => this.updateAllPrices());
        project.on('propertyChanged', () => this.updateAllPrices());
        project.on('sizeChanged', () => {
            console.log('size changed, updating total');
            this.updateAllPrices();
        });
        project.on('widthChanged', () => this.updateAllPrices());
        project.on('heightChanged', () => this.updateAllPrices());
        project.on('modulesFormatChanged', () => this.updateAllPrices());
        project.on('facesCountChanged', () => this.updateAllPrices());
        project.on('digital', () => this.updateAllPrices());
        project.on('material', () => this.updateAllPrices());
        this.updateAllPrices();
    }
    setPrice(property, price, value) {
        this.prices.set(property, price);
        this.project.setPropertyPrice(property, price);
        this.emit('changed', { property, price, value });
    }
    updateAllPrices() {
        for (const key of this.calculator.getAllAvailableProperties()) {
            this.updatePropertyPrice(key);
        }
        for (const property of this.calculator.getAllHiddenProperties()) {
            this.setPrice(property, 0.0, -1);
        }
    }
    updatePropertyPrice(property) {
        if (!this.calculator.getAllAvailableProperties().includes(property)) {
            this.setPrice(property, 0.0, -1);
            return 0.0;
        }
        const step = this.calculator.getPropertyStep(property);
        let id = this.project.getPropertyId(property);
        if (Array.isArray(step.variants)) {
            //default value
            if (typeof id !== 'number' || id === -1 || id === null) {
                id = this.calculator.getDefaultProjectPropertyId(property);
            }
        }
        else {
            //for not checked, with only one value available
            if ((typeof id !== 'number' || id === -1 || id === null) && (typeof id !== 'boolean')) {
                id = this.calculator.getPropertyOnlyValueId(property);
            }
        }
        if ((typeof id === 'number' && id > -1) || (typeof id === 'boolean' && id)) {
            const price = this.getForProperty(property, id);
            this.setPrice(property, price, id);
            return price;
        }
        else {
            this.setPrice(property, 0.0, id);
            return 0.0;
        }
    }
    evaluate(formula, scope) {
        try {
            return evaluate(formula, scope);
        }
        catch (e) {
            return 0.0;
        }
    }
    createPriceCalculationScope(projectPriceScope) {
        const { property, propertyValue, step, width, height, square, perimeter, countOfFaces, } = projectPriceScope;
        const scope = {
            optionName: property,
            optionValue: propertyValue || step,
            width,
            height,
            square,
            perimeter,
            countOfFaces,
            thisPriceFormula: '0',
            base: 0,
            value: 0,
            fixed: 0,
            overBase: 0,
            optionPrice: 0,
            option: step
        };
        return scope;
    }
    getForProperty(property, id) {
        if (!this.calculator.getAllAvailableProperties().includes(property)) {
            this.setPrice(property, 0.0, -1);
            return 0.0;
        }
        if (PRICE_ALWAYS_ZERO.indexOf(property) > -1) {
            return 0.0;
        }
        const step = this.project.getPropertyStep(property);
        const propertyValue = PRICE_ONLY_IN_STEP.includes(property) ? false : this.project.getPropertyValue(property, id);
        const countOfFaces = this.project.getFacesCount();
        let price = 0.0;
        this.project.geometry.getAll().forEach((moduleGeometry) => {
            const innerSize = moduleGeometry.getInnerCompositeSize(), width = innerSize.width, height = innerSize.height, square = moduleGeometry.getSquare(), perimeter = moduleGeometry.getPerimeter();
            const scope = this.createPriceCalculationScope({
                property,
                propertyValue,
                step, width,
                height,
                square,
                perimeter,
                countOfFaces,
            });
            if (property === 'baget') {
                price += this.getPriceForBaget(scope);
                return;
            }
            if (this.isDigital()) {
                price += this.getForPropertyDigital(scope);
            }
            else {
                price += this.getForPropertyMaterial(scope);
            }
        });
        return price;
    }
    getForPropertyDigital(scope) {
        return this.getForPropertyMedia(scope, 'Digital');
    }
    getForPropertyMaterial(scope) {
        return this.getForPropertyMedia(scope);
    }
    getForPropertyMedia(scope, media = '') {
        const valuePrice = scope.optionValue[`price${media}`];
        this.updateScopeFromStep(scope, media);
        if (!isNaN(valuePrice)) {
            scope.price = valuePrice;
        }
        this.updateScopeFromStepVariant(scope, media);
        if (Object.prototype.hasOwnProperty.call(scope.option, `overBasePrice${media}`) && !isNaN(scope.option[`overBasePrice${media}`])) {
            scope.overBase = scope.option[`overBasePrice${media}`];
        }
        if (Object.prototype.hasOwnProperty.call(scope.option, 'variants') && !Array.isArray(scope.option.variants)) {
            if (Object.prototype.hasOwnProperty.call(scope.option.variants, `price${media}`)) {
                scope.price = scope.option.variants[`price${media}`];
            }
        }
        if (['face', 'designOilPaint'].includes(scope.optionName)) {
            scope.value = scope.countOfFaces;
        }
        if ((scope.optionName === 'underframeThickness') && (scope.optionValue.price === 0)) {
            return 0.0;
        }
        else {
            return this.evaluate(scope.thisPriceFormula, scope);
        }
    }
    getPriceForBaget(scope) {
        this.updateScopeFromStep(scope);
        for (const baget of scope.option.variants) {
            scope.price = baget.price;
            this.pricesOfBagets.set(`${baget.id}`, this.evaluate(scope.thisPriceFormula, scope));
        }
        const res = this.pricesOfBagets.get(`${scope.optionValue.id}`);
        this.emit('bagets.changed', this.pricesOfBagets);
        if (res) {
            return res;
        }
        else {
            return 0.0;
        }
    }
    isDigital() {
        return this.project.isDigital();
    }
    isDesignStyled() {
        return this.project.isDesignStyled();
    }
    updateScopeFromStep(scope, priceMod = '') {
        this.updateScopeFromSource(scope, priceMod, 'option');
    }
    updateScopeFromStepVariant(scope, priceMod = '') {
        this.updateScopeFromSource(scope, priceMod, 'optionValue');
    }
    updateScopeFromSource(scope, priceMod = '', source = 'option') {
        const priceName = 'price' + priceMod;
        if (scope[source] && scope[source][priceName]) {
            const price = scope[source][priceName];
            if (Object.prototype.hasOwnProperty.call(price, 'formula') && price.formula) {
                scope.thisPriceFormula = price.formula;
                if (price[`fixed`]) {
                    scope.price = parseFloat(price[`fixed`]);
                }
                if (price[`base`]) {
                    scope.base = parseFloat(price[`base`]);
                }
                if (typeof price[`overBase`] !== 'undefined') {
                    scope.overBase = parseFloat(price[`overBase`]);
                }
            }
        }
        if (scope.option.optionName === 'face') {
            if (this.isDesignStyled()) {
                const style = this.getDesignStyleValue();
                if (style) {
                    scope.overBase = this.getDesignStylePrice(style, 'overBase', priceMod);
                    scope.base = this.getDesignStylePrice(style, 'base', priceMod);
                }
            }
        }
    }
    getDesignStyleOverBasePrice(designStyleStep) {
        if (this.isDigital()) {
            if (designStyleStep.overBasePriceDigital) {
                return parseFloat(designStyleStep.overBasePriceDigital);
            }
        }
        else {
            if (designStyleStep.overBasePrice) {
                return parseFloat(designStyleStep.overBasePrice);
            }
        }
        return 0;
    }
    getDesignStylePrice(designStyleStep, type = '', media = '') {
        let priceMod = `price${media}`;
        if (type !== 'base' && type !== '') {
            priceMod = `${type}Price${media}`;
        }
        const price = designStyleStep[priceMod];
        if (price) {
            return price;
        }
        else {
            return 0;
        }
    }
    getDesignStyleValue() {
        return this.project.getDesignStyleValue();
    }
}
__decorate([
    DefaultReturn(0.0),
    Reportable
], CalculatorPrice.prototype, "updatePropertyPrice", null);
__decorate([
    Reportable
], CalculatorPrice.prototype, "evaluate", null);
__decorate([
    DefaultReturn(0.0),
    Reportable
], CalculatorPrice.prototype, "getForProperty", null);
export default CalculatorPrice;
